import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { Upsell } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import type { AssistantThreadRead } from '../../types/api';
import { useBusiness } from '../onboarding/useBusiness';
import { readAssistantPrefs, writeAssistantPrefs } from './assistantStorage';
import { AssistantConversation } from './AssistantConversation';
import { AssistantThreadList } from './AssistantThreadList';
import { AssistantCreditsMeter } from './AssistantCreditsMeter';
import { useAssistantThread } from './useAssistantThread';
import { useAssistantThreads, threadLabel } from './useAssistantThreads';
import { useClientActionRunner } from './useClientActionRunner';

const TITLE = 'AI assistant';
const LEDE = 'Ask, plan and find your way around. It reads your brand and your work.';

/** `/assistant?new=1`: an empty conversation, its thread made by the first send. */
const NEW_CHAT_PATH = '/assistant?new=1';

/**
 * `/assistant`, gated on the plan. Without the assistant the page is an empty
 * state with the Upsell (the palette's "AI assistant" lands here). While the
 * plan is still being read nothing mounts, so a plan without the assistant
 * never opens or creates a chat on the way to the Upsell.
 */
export function AssistantPage() {
  const { can, loading } = usePlan();

  if (loading) {
    return (
      <main className="page">
        <PageHeader title={TITLE} lede={LEDE} />
        <p className="kb-section__status" role="status">
          Loading
        </p>
      </main>
    );
  }

  if (!can('assistant')) {
    return (
      <main className="page">
        <PageHeader title={TITLE} lede={LEDE} />
        <div className="upsell-empty">
          <Upsell feature="assistant" />
        </div>
      </main>
    );
  }

  return <AssistantWorkspace />;
}

/**
 * The assistant's full page, `/assistant/:threadId?`: every chat on the left,
 * the same conversation as the bubble on the right. It took over from the AI
 * Planner (`/planner` redirects here); planning happens in the conversation,
 * as proposal cards applied through the calendar's own endpoints.
 *
 * Bare `/assistant` opens the chat the bubble was last on, else the newest,
 * else a new one. Whatever chat this page shows becomes the bubble's.
 */
function AssistantWorkspace() {
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();
  const { threadId: routeThreadId = null } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const wantsNew = routeThreadId === null && searchParams.get('new') === '1';

  const threads = useAssistantThreads(businessId);
  const chat = useAssistantThread(businessId, routeThreadId);
  useClientActionRunner(chat.messages);

  const [isListOpen, setIsListOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // The chat on screen is the one the bubble continues on every other page.
  const loadedThreadId = chat.thread?.id ?? null;
  useEffect(() => {
    if (!businessId || loadedThreadId === null) return;
    writeAssistantPrefs(businessId, {
      ...readAssistantPrefs(businessId),
      threadId: loadedThreadId,
    });
  }, [businessId, loadedThreadId]);

  // A landed reply may have named the chat and moved it up the list.
  const latestArrivalId = chat.latestArrivalId;
  const refreshThreads = threads.refresh;
  useEffect(() => {
    if (latestArrivalId !== null) refreshThreads();
  }, [latestArrivalId, refreshThreads]);

  // A fresh chat starts with the cursor in the box.
  useEffect(() => {
    if (wantsNew) composerRef.current?.focus();
  }, [wantsNew]);

  if (isBusinessLoading) {
    return (
      <main className="page">
        <PageHeader title={TITLE} lede={LEDE} />
        <p className="kb-section__status" role="status">
          Loading your brand
        </p>
      </main>
    );
  }

  if (!businessId) {
    return (
      <main className="page">
        <PageHeader title={TITLE} lede={LEDE} />
        <div className="kb-section">
          <p className="form__notice">
            {businessError ?? 'No brand is set up on this account yet. Finish setup first.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      </main>
    );
  }

  if (routeThreadId === null && !wantsNew && !threads.isLoading) {
    const stored = readAssistantPrefs(businessId).threadId;
    const opening =
      threads.threads.find((thread) => thread.id === stored)?.id ?? threads.threads[0]?.id;
    if (opening) return <Navigate to={`/assistant/${opening}`} replace />;
  }

  function onThreadCreated(thread: AssistantThreadRead) {
    void navigate(`/assistant/${thread.id}`, { replace: true });
  }

  const isDeciding = routeThreadId === null && !wantsNew && threads.isLoading;
  const heading =
    routeThreadId === null || chat.notFound
      ? 'New chat'
      : chat.thread
        ? threadLabel(chat.thread)
        : 'Chat';

  return (
    // assistant-screen: the page fills the main column, so the conversation
    // takes the height left and the transcript is the one scroll.
    <main className="page page--wide assistant-screen">
      <PageHeader
        title={TITLE}
        lede={LEDE}
        meta={
          chat.credits ? <AssistantCreditsMeter credits={chat.credits} variant="page" /> : undefined
        }
      />

      <div className="page__actions assistant-page__actions">
        <button
          className="button assistant-page__toggle"
          type="button"
          aria-expanded={isListOpen}
          onClick={() => setIsListOpen((open) => !open)}
        >
          {isListOpen ? 'Hide chats' : 'Chats'}
        </button>
      </div>

      <div className={isListOpen ? 'assistant-page assistant-page--open' : 'assistant-page'}>
        <AssistantThreadList
          threads={threads}
          activeThreadId={routeThreadId}
          onNew={() => {
            setIsListOpen(false);
            void navigate(NEW_CHAT_PATH);
            composerRef.current?.focus();
          }}
          onFollow={() => setIsListOpen(false)}
          onRemoved={(nextThreadId) => {
            void navigate(nextThreadId === null ? NEW_CHAT_PATH : `/assistant/${nextThreadId}`, {
              replace: true,
            });
          }}
        />

        <section className="assistant-page__chat" aria-label={heading}>
          <div className="assistant-page__head">
            <h2 className="assistant-page__title">{heading}</h2>
          </div>

          {isDeciding ? (
            <p className="kb-section__status" role="status">
              Opening the AI assistant
            </p>
          ) : routeThreadId !== null && chat.notFound ? (
            <div className="assistant-page__gone">
              <p>This chat is no longer there.</p>
              <button
                className="button button--primary"
                type="button"
                onClick={() => void navigate(NEW_CHAT_PATH, { replace: true })}
              >
                Start a new chat
              </button>
            </div>
          ) : (
            <AssistantConversation
              businessId={businessId}
              threadId={routeThreadId}
              chat={chat}
              createThread={threads.create}
              onThreadCreated={onThreadCreated}
              onSent={threads.refresh}
              variant="page"
              composerRef={composerRef}
            />
          )}
        </section>
      </div>
    </main>
  );
}
